import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Category, PostSummary } from '@/types'

import { Fragment, useMemo, useState } from 'react'
import { Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getCategories, getPosts } from '@/lib/content'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { FinalCta } from '@/components/kit/final-cta'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { SplitText } from '@/components/kit/split-text'
import { FeaturedPost } from '@/components/blog/featured-post'
import { NewsletterBlock } from '@/components/blog/newsletter-block'
import { PostCard } from '@/components/blog/post-card'

interface JournalProps {
    posts: Array<PostSummary>
    categories: Array<Category>
}

const PAGE_SIZE = 6

const Journal: FC<JournalProps> = ({ posts, categories }): ReactNode => {
    const [category, setCategory] = useState('all')
    const [query, setQuery] = useState('')
    const [limit, setLimit] = useState(PAGE_SIZE)

    const featured = posts.find(post => post.featured) ?? posts[0] ?? null
    const rest = posts.filter(post => post.id !== featured?.id)
    const searching = query.trim().length > 0 || category !== 'all'
    const pool = searching ? posts : rest

    const results = useMemo(() => {
        const needle = query.trim().toLowerCase()

        return pool.filter(post => {
            const inCategory = category === 'all' || post.category?.slug === category

            if (!inCategory) return false
            if (!needle) return true

            const haystack = [post.title, post.excerpt ?? '', post.category?.name ?? '', post.author?.name ?? '', ...post.hashtags]
                .join(' ')
                .toLowerCase()

            return haystack.includes(needle)
        })
    }, [pool, category, query])

    const shown = results.slice(0, limit)
    const remaining = results.length - shown.length

    const reset = () => {
        setCategory('all')
        setQuery('')
        setLimit(PAGE_SIZE)
    }

    return (
        <Layout>
            <Seo
                title='Journal'
                description='Forecasts, playbooks and case notes from the Updraft studio. Short, useful reads on hooks, voice, posting times and what the feed wants next.'
            />

            <section
                aria-labelledby='journal-title'
                className='relative overflow-hidden pb-12 pt-36 md:pt-44'
            >
                <div
                    aria-hidden='true'
                    className='bg-grid mask-radial absolute inset-0'
                />

                <div className='relative mx-auto max-w-[1400px] px-5 md:px-10'>
                    <div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
                        <div>
                            <SectionLabel>The Updraft journal · read it on the bus</SectionLabel>
                            <SplitText
                                as='h1'
                                id='journal-title'
                                lines={['Notes from the', 'weather desk']}
                                italic='weather'
                                className='mt-6 text-[clamp(3.25rem,9.5vw,8.5rem)] font-bold leading-[0.86] tracking-[-0.045em]'
                            />
                        </div>
                        <Reveal
                            as='p'
                            delay={250}
                            className='max-w-sm text-lg leading-relaxed text-stone'
                        >
                            Forecasts, playbooks and case notes, written by the people who make the posts. Every one of them short enough to finish before your coffee goes cold.
                        </Reveal>
                    </div>

                    {featured && (
                        <Reveal
                            delay={150}
                            className='mt-14'
                        >
                            <FeaturedPost post={featured} />
                        </Reveal>
                    )}
                </div>
            </section>

            <section
                aria-label='All stories'
                className='mx-auto max-w-[1400px] px-5 pb-10 md:px-10'
            >
                <div className='sticky top-[4.75rem] z-30 -mx-2 mb-12 rounded-[1.75rem] border border-black/5 bg-cream/85 p-2 backdrop-blur-xl md:top-24'>
                    <div className='flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between'>
                        <div
                            role='group'
                            aria-label='Filter by category'
                            className='no-scrollbar flex gap-1.5 overflow-x-auto'
                        >
                            <button
                                type='button'
                                aria-pressed={category === 'all'}
                                onClick={() => {
                                    setCategory('all')
                                    setLimit(PAGE_SIZE)
                                }}
                                className={cn(
                                    'shrink-0 rounded-full px-4 py-2.5 text-sm font-medium transition-colors duration-300',
                                    category === 'all' ? 'bg-ink text-cream' : 'text-ink hover:bg-sand'
                                )}
                            >
                                All
                            </button>

                            {categories.map(item => (
                                <button
                                    key={item.id}
                                    type='button'
                                    aria-pressed={category === item.slug}
                                    onClick={() => {
                                        setCategory(item.slug)
                                        setLimit(PAGE_SIZE)
                                    }}
                                    className={cn(
                                        'shrink-0 rounded-full px-4 py-2.5 text-sm font-medium transition-colors duration-300',
                                        category === item.slug ? 'bg-ink text-cream' : 'text-ink hover:bg-sand'
                                    )}
                                >
                                    <span data-mdlfy-cms={`categories:${item.id}:name`}>{item.name}</span>
                                </button>
                            ))}
                        </div>

                        <label className='relative flex items-center lg:w-80'>
                            <span className='sr-only'>Search the journal</span>
                            <Search
                                aria-hidden='true'
                                className='pointer-events-none absolute left-4 size-4 text-stone'
                            />
                            <input
                                type='search'
                                value={query}
                                onChange={event => {
                                    setQuery(event.target.value)
                                    setLimit(PAGE_SIZE)
                                }}
                                placeholder='Search hooks, voice, posting times'
                                className='h-11 w-full rounded-full border border-ink/10 bg-paper pl-11 pr-10 text-[15px] outline-none transition-colors placeholder:text-ink/40 focus:border-ember [&::-webkit-search-cancel-button]:hidden'
                            />
                            {query && (
                                <button
                                    type='button'
                                    onClick={() => setQuery('')}
                                    aria-label='Clear search'
                                    className='absolute right-2 flex size-7 items-center justify-center rounded-full text-stone hover:bg-sand hover:text-ink'
                                >
                                    <X className='size-4' />
                                </button>
                            )}
                        </label>
                    </div>
                </div>

                <p
                    className='sr-only'
                    aria-live='polite'
                >
                    {results.length === 1 ? '1 story found' : `${results.length} stories found`}
                </p>

                {results.length ? (
                    <div className='grid gap-x-6 gap-y-14 md:grid-cols-2 lg:grid-cols-3'>
                        {shown.map((post, index) => (
                            <Fragment key={post.id}>
                                <Reveal delay={(index % 3) * 90}>
                                    <PostCard
                                        post={post}
                                        headingLevel='h2'
                                    />
                                </Reveal>

                                {index === 2 && shown.length > 3 && (
                                    <Reveal className='md:col-span-2 lg:col-span-3'>
                                        <NewsletterBlock source='journal-grid' />
                                    </Reveal>
                                )}
                            </Fragment>
                        ))}
                    </div>
                ) : posts.length ? (
                    <div className='rounded-[2rem] border border-dashed border-ink/20 px-6 py-16 text-center'>
                        <p className='font-heading text-3xl font-bold tracking-[-0.04em]'>No stories under that one. Yet.</p>
                        <p className='mt-3 text-stone'>Try another word, or clear the filters and browse the lot.</p>
                        <button
                            type='button'
                            onClick={reset}
                            className='mt-6 rounded-full bg-ink px-5 py-3 text-sm font-medium text-cream transition-colors hover:bg-black'
                        >
                            Show every story
                        </button>
                    </div>
                ) : (
                    <div className='rounded-[2rem] border border-dashed border-ink/20 px-6 py-16 text-center'>
                        <p className='font-heading text-3xl font-bold tracking-[-0.04em]'>The presses are warming up.</p>
                        <p className='mt-3 text-stone'>Sign up to the Tuesday Signal and the first story comes straight to you.</p>
                    </div>
                )}

                {remaining > 0 && (
                    <div className='mt-16 flex flex-col items-center gap-3'>
                        <button
                            type='button'
                            onClick={() => setLimit(value => value + 3)}
                            className='group inline-flex h-14 items-center gap-3 rounded-full border border-ink/15 bg-paper px-7 text-base font-medium transition-colors hover:border-ink hover:bg-ink hover:text-cream'
                        >
                            More stories
                            <span className='font-mono text-xs text-stone group-hover:text-cream/70'>
                                {remaining} more
                            </span>
                        </button>
                    </div>
                )}

                {shown.length > 0 && shown.length <= 3 && (
                    <NewsletterBlock
                        source='journal-grid'
                        className='mt-16'
                    />
                )}
            </section>

            <FinalCta
                lines={['Rather we wrote', 'about your brand?']}
                italic='your'
                body='Every case note started as a conversation. Tell us what you are working on and we will send a free forecast within 48 hours.'
            />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<JournalProps> = async () => {
    try {
        const [posts, categories] = await Promise.all([getPosts(), getCategories()])

        return { props: { posts, categories }, revalidate: 60 }
    }
    catch (error) {
        console.error('Journal content could not be loaded:', error instanceof Error ? error.message : error)

        return { props: { posts: [], categories: [] }, revalidate: 30 }
    }
}

export default Journal
